import { useCallback, useState } from 'react';

import { api, type RecruiterDetail, type RecruiterRow, type Summary } from './api';
import { BarList, BarListSummary } from './components/Charts';
import { Modal } from './components/Modal';
import { PlanTierCard, type ScheduleRates } from './components/PlanTierCard';
import {
  Card,
  CardHeader,
  Cell,
  ExportButton,
  Kpi,
  Row,
  SearchBar,
  Spinner,
  Table,
} from './components/Ui';
import { UnifiedTimeFilter } from './components/UnifiedTimeFilter';
import { useTimeFilter } from './context/TimeFilterContext';
import { useAsyncData } from './hooks/useAsyncData';
import { useSortedRows } from './hooks/useSortedRows';
import { MiniBarChart } from './components/KpiCard';
import { hours, money, moneyCompact } from './format';
import { Shell, type ShellProps } from './Shell';

interface Data {
  readonly summary: Summary;
  readonly recruiters: readonly RecruiterRow[];
}

export function Recruiters(props: ShellProps) {
  const { navigate } = props;
  const [search, setSearch] = useState('');
  const tf = useTimeFilter();

  // Plan tier popup modal state
  const [selectedEarner, setSelectedEarner] = useState<RecruiterRow | null>(null);
  const [earnerDetail, setEarnerDetail] = useState<RecruiterDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  async function openPlanModal(row: RecruiterRow) {
    setSelectedEarner(row);
    setEarnerDetail(null);
    setDetailLoading(true);
    try {
      const detail = await api.recruiter(row.id, tf.toDateRange(), row.planId ?? undefined);
      setEarnerDetail(detail);
    } catch {
      setEarnerDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  function closePlanModal() {
    setSelectedEarner(null);
    setEarnerDetail(null);
  }

  const { data, error } = useAsyncData<Data>(
    () => {
      const dateRange = tf.toDateRange();
      return Promise.all([api.summary(dateRange), api.recruiters(dateRange)]).then(
        ([summary, recruiters]) => ({ summary, recruiters }),
      );
    },
    [tf.selection],
    'Could not load commission earner data.',
  );

  const accessor = useCallback(
    (row: RecruiterRow, key: string): unknown => {
      if (key === 'avgGp')
        return row.contractors > 0 ? Number(row.grossProfit) / row.contractors : 0;
      const map: Record<string, unknown> = {
        name: row.name,
        plan: row.planName ?? '',
        contractors: row.contractors,
        hours: row.hours,
        grossProfit: row.grossProfit,
        commission: row.commission,
      };
      return map[key];
    },
    [data],
  );

  const filtered =
    data !== null
      ? data.recruiters.filter(
          (r) => search === '' || r.name.toLowerCase().includes(search.toLowerCase()),
        )
      : [];

  const { sorted, sortKey, sortDirection, onSort } = useSortedRows(filtered, accessor);

  if (error !== null) {
    return (
      <Shell {...props}>
        <Card className="px-5 py-8">
          <p className="text-sm text-caution">{error}</p>
        </Card>
      </Shell>
    );
  }

  if (data === null) {
    return (
      <Shell {...props}>
        <Card>
          <Spinner label="Loading commission earner data…" />
        </Card>
      </Shell>
    );
  }

  const { recruiters } = data;
  const uniqueEarnerCount = new Set(recruiters.map((r) => r.id)).size;
  const totalGp = recruiters.reduce((s, r) => s + Number(r.grossProfit), 0).toFixed(4);
  const totalHours = recruiters.reduce((s, r) => s + Number(r.hours), 0).toFixed(4);
  const totalContractors = recruiters.reduce((s, r) => s + r.contractors, 0);
  const totalCommission = recruiters.reduce((s, r) => s + Number(r.commission), 0).toFixed(4);

  return (
    <Shell {...props}>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-ink">Commission Earner Performance</h1>
          <p className="mt-1 text-sm text-ink-faint">
            Commission attribution and gross profit by commission earner
          </p>
        </div>
        <UnifiedTimeFilter />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Kpi
          label="Total earners"
          value={String(uniqueEarnerCount)}
          hint={`${totalContractors} contractor assignments across ${String(recruiters.length)} plan rows`}
          delay={0}
        />
        <Kpi
          label="Attributed GP"
          value={moneyCompact(totalGp)}
          exact={money(totalGp)}
          tone="profit"
          delay={50}
        />
        <Kpi
          label="Total commission"
          value={moneyCompact(totalCommission)}
          exact={money(totalCommission)}
          tone="revenue"
          delay={75}
        />
        <Kpi label="Total hours" value={hours(totalHours)} delay={100} />
        <Kpi
          label="Avg GP per earner"
          value={
            recruiters.length > 0
              ? moneyCompact((Number(totalGp) / recruiters.length).toFixed(4))
              : '—'
          }
          tone="profit"
          delay={150}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card delay={200}>
          <CardHeader title="GP by commission earner" subtitle="Click to view detail" />
          <BarListSummary
            viz={
              <MiniBarChart
                bars={recruiters.map((r) => ({
                  label: r.name,
                  value: Number(r.grossProfit),
                }))}
              />
            }
          >
            <span>
              <span className="figure font-semibold">{moneyCompact(totalGp)}</span> GP
            </span>
            <span>
              <span className="figure font-semibold">{moneyCompact(totalCommission)}</span>{' '}
              commission
            </span>
            <span>
              {recruiters.length} earner{recruiters.length !== 1 ? 's' : ''}
            </span>
          </BarListSummary>
          <BarList
            data={(() => {
              // Aggregate by person for the bar chart (one bar per earner, regardless of plan count)
              const byPerson = new Map<
                string,
                { name: string; gp: number; contractors: number; hrs: number }
              >();
              for (const r of recruiters) {
                const existing = byPerson.get(r.id);
                if (existing) {
                  existing.gp += Number(r.grossProfit);
                  existing.contractors += r.contractors;
                  existing.hrs += Number(r.hours);
                } else {
                  byPerson.set(r.id, {
                    name: r.name,
                    gp: Number(r.grossProfit),
                    contractors: r.contractors,
                    hrs: Number(r.hours),
                  });
                }
              }
              return [...byPerson.entries()].map(([, v]) => ({
                label: v.name,
                value: v.gp.toFixed(4),
                note: `${v.contractors} contractors · ${hours(v.hrs.toFixed(4))} h`,
              }));
            })()}
            onClick={(index) => {
              const ids = [...new Set(recruiters.map((r) => r.id))];
              const id = ids[index];
              if (id !== undefined) navigate(`recruiter/${id}`);
            }}
          />
        </Card>

        <Card className="lg:col-span-2" delay={250}>
          <CardHeader
            title="Earner detail"
            subtitle="Click a row to see full commission breakdown"
            aside={
              <div className="flex items-center gap-3">
                <div className="w-48">
                  <SearchBar value={search} onChange={setSearch} placeholder="Filter earners…" />
                </div>
                <ExportButton
                  data={sorted.map((r) => ({
                    name: r.name,
                    role: r.role === 'salesperson' ? 'Salesperson' : 'Recruiter',
                    plan: r.planName ?? '—',
                    contractors: r.contractors,
                    hours: hours(r.hours),
                    grossProfit: money(r.grossProfit),
                    commission: money(r.commission),
                    avgGpPerContractor:
                      r.contractors > 0
                        ? money((Number(r.grossProfit) / r.contractors).toFixed(4))
                        : '—',
                  }))}
                  filename="recruiters"
                  headers={[
                    { key: 'name', label: 'Name' },
                    { key: 'role', label: 'Role' },
                    { key: 'plan', label: 'Plan' },
                    { key: 'contractors', label: 'Contractors' },
                    { key: 'hours', label: 'Hours' },
                    { key: 'grossProfit', label: 'Gross Profit' },
                    { key: 'commission', label: 'Commission' },
                    { key: 'avgGpPerContractor', label: 'Avg GP/Contractor' },
                  ]}
                />
              </div>
            }
          />
          <Table
            head={[
              { label: 'Name', sortKey: 'name' },
              { label: 'Role' },
              { label: 'Plan', sortKey: 'plan' },
              { label: 'Contractors', align: 'right', sortKey: 'contractors' },
              { label: 'Hours', align: 'right', sortKey: 'hours' },
              { label: 'Gross profit', align: 'right', sortKey: 'grossProfit' },
              { label: 'Commission', align: 'right', sortKey: 'commission' },
              { label: 'Avg GP/contractor', align: 'right', sortKey: 'avgGp' },
            ]}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={onSort}
          >
            {sorted.map((row) => {
              const rowKey = row.planId ? `${row.id}:${row.planId}` : row.id;
              const planQuery = row.planId ? `?planId=${row.planId}` : '';
              return (
                <Row key={rowKey} onClick={() => openPlanModal(row)}>
                  <Cell>
                    <span
                      className="group inline-flex items-center gap-1.5 cursor-pointer text-ink transition-colors hover:text-profit"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`recruiter/${row.id}${planQuery}`);
                      }}
                    >
                      <svg
                        className="h-3 w-3 shrink-0 text-ink-faint/50 transition-colors group-hover:text-profit"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5 10a.75.75 0 01.75-.75h6.638L10.23 7.29a.75.75 0 111.04-1.08l3.5 3.25a.75.75 0 010 1.08l-3.5 3.25a.75.75 0 11-1.04-1.08l2.158-1.96H5.75A.75.75 0 015 10z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {row.name}
                    </span>
                  </Cell>
                  <Cell>
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        row.role === 'salesperson'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {row.role === 'salesperson' ? 'Sales' : 'Recruiter'}
                    </span>
                  </Cell>
                  <Cell>
                    {row.planName ? (
                      <span className="text-xs text-ink-muted">{row.planName}</span>
                    ) : (
                      <span className="text-xs text-ink-faint">—</span>
                    )}
                  </Cell>
                  <Cell align="right" mono>
                    {row.contractors}
                  </Cell>
                  <Cell align="right" mono>
                    {hours(row.hours)}
                  </Cell>
                  <Cell align="right" mono>
                    <span className="text-profit">{money(row.grossProfit)}</span>
                  </Cell>
                  <Cell align="right" mono>
                    <span className="text-revenue">{money(row.commission)}</span>
                  </Cell>
                  <Cell align="right" mono>
                    {row.contractors > 0
                      ? money((Number(row.grossProfit) / row.contractors).toFixed(4))
                      : '—'}
                  </Cell>
                </Row>
              );
            })}
          </Table>
        </Card>
      </div>

      {/* Commission plan tier popup */}
      <Modal
        open={selectedEarner !== null}
        onClose={closePlanModal}
        title={selectedEarner?.name ?? ''}
        subtitle={
          selectedEarner
            ? `${selectedEarner.role === 'salesperson' ? 'Salesperson' : 'Recruiter'} · ${selectedEarner.planName ?? 'No plan'}`
            : undefined
        }
        size="md"
      >
        {detailLoading ? (
          <div className="flex items-center gap-3 py-10 text-sm text-ink-faint">
            <span className="h-3 w-3 animate-pulse rounded-full bg-revenue-dim" />
            Loading plan details…
          </div>
        ) : earnerDetail !== null ? (
          (() => {
            const creditedGp = earnerDetail.contractors.reduce(
              (sum, c) => sum + Number(c.creditedGp),
              0,
            );
            const activeContractors = earnerDetail.contractors.filter(
              (c) => Number(c.creditedGp) !== 0,
            ).length;
            return (
              <div className="space-y-5">
                {/* GP and Commission summary */}
                <div className="flex flex-wrap gap-6 rounded-lg bg-surface-overlay/60 px-4 py-3">
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-ink-faint">
                      Gross Profit
                    </p>
                    <p className="figure text-lg font-semibold text-profit">
                      {money(
                        earnerDetail.contractors
                          .reduce((s, c) => s + Number(c.grossProfit), 0)
                          .toFixed(4),
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-ink-faint">
                      Credited GP
                    </p>
                    <p className="figure text-lg font-semibold text-profit">
                      {money(creditedGp.toFixed(4))}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-ink-faint">
                      Commission
                    </p>
                    <p className="figure text-lg font-semibold text-revenue">
                      {money(earnerDetail.commission)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-ink-faint">
                      Contractors
                    </p>
                    <p className="figure text-lg font-semibold text-ink">
                      {String(activeContractors)}
                    </p>
                  </div>
                </div>

                <PlanTierCard
                  tiers={earnerDetail.tiers}
                  tierMode={earnerDetail.tierMode}
                  currentGp={creditedGp}
                  planName={earnerDetail.planName}
                  elevatedPeriodMonths={earnerDetail.elevatedPeriodMonths}
                  schedules={earnerDetail.schedules.map((s): ScheduleRates => ({
                    name: s.name,
                    isElevated: s.isElevated,
                    rates: s.rates,
                  }))}
                />
              </div>
            );
          })()
        ) : (
          <p className="py-6 text-center text-sm text-caution">Could not load plan details.</p>
        )}
      </Modal>
    </Shell>
  );
}
